import React, { useState, useEffect, useRef } from 'react';
import { citasAPI, clientesAPI, consultasAPI, authAPI } from '../services/api';
import ExcelJS from 'exceljs';
import { getLabels } from '../utils/userLabels';
import './ResumenSesiones.css';

function ResumenSesiones() {
  const [tipoUsuario, setTipoUsuario] = useState('coach');

  useEffect(() => {
    authAPI.check().then(res => {
      if (res.data.authenticated && res.data.user) {
        setTipoUsuario(res.data.user.tipo_usuario || 'coach');
      }
    }).catch(() => {});
  }, []);

  const labels = getLabels(tipoUsuario);
  const [citas, setCitas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [sortField, setSortField] = useState('fecha');
  const [sortDirection, setSortDirection] = useState('asc'); // Cambiado a 'asc' para empezar por la más antigua
  const rowRefs = useRef({}); // Referencias a las filas para mantener la posición
  const lastEditedRowId = useRef(null); // ID de la última fila editada

  useEffect(() => {
    loadClientes();
    loadCitas();
  }, []);

  const loadClientes = async () => {
    try {
      const response = await clientesAPI.getAll(true);
      setClientes(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadCitas = async () => {
    try {
      setLoading(true);
      const response = await citasAPI.getAll();
      setCitas(response.data);
      setError(null);
    } catch (err) {
      setError('Error al cargar las citas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getClienteNombre = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    return cliente ? `${cliente.nombre} ${cliente.apellidos}` : 'Cliente no encontrado';
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    // fecha puede ser fecha_hora (datetime) o fecha_consulta (date)
    const date = new Date(fecha);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDuracion = (minutos) => {
    if (!minutos) return '-';
    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;
    if (horas === 0) {
      return `${mins}m`;
    }
    if (mins === 0) {
      return `${horas}h`;
    }
    return `${horas}h ${mins}m`;
  };

  const formatTiempo = (minutos) => {
    if (!minutos || minutos === 0) return '0h 0m';
    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;
    if (horas === 0) {
      return `${mins}m`;
    }
    if (mins === 0) {
      return `${horas}h`;
    }
    return `${horas}h ${mins}m`;
  };

  // Calcular total de horas realizadas (solo sesiones completadas)
  const calcularHorasTotales = () => {
    const citasCompletadas = citas.filter(cita => 
      (cita.estado || '').toLowerCase() === 'completada'
    );
    
    const totalMinutos = citasCompletadas.reduce((total, cita) => {
      const duracion = cita.duracion_minutos || cita.duracion || 0;
      return total + (parseInt(duracion) || 0);
    }, 0);
    
    return totalMinutos;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedCitas = () => {
    const sorted = [...citas].sort((a, b) => {
      let aValue, bValue;
      
      if (sortField === 'fecha') {
        aValue = new Date(a.fecha_hora);
        bValue = new Date(b.fecha_hora);
      } else if (sortField === 'cliente') {
        aValue = getClienteNombre(a.cliente_id).toLowerCase();
        bValue = getClienteNombre(b.cliente_id).toLowerCase();
      } else {
        return 0;
      }
      
      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    return sorted;
  };

  const handleCellClick = (citaId, field, currentValue) => {
    setEditingCell({ citaId, field });
    // Para el campo entregado_documento_seguimiento, convertir a string '0' o '1'
    if (field === 'entregado_documento_seguimiento') {
      setEditValue((currentValue === 1 || currentValue === true || currentValue === '1' || currentValue === 'true') ? '1' : '0');
    } else {
      setEditValue(currentValue || '');
    }
  };

  const handleCellBlur = async () => {
    if (!editingCell) return;

    const { citaId, field } = editingCell;
    const cita = citas.find(c => c.id === citaId);
    
    if (!cita) return;

    let valueToSave;
    if (field === 'entregado_documento_seguimiento') {
      // Convertir el valor del select a número (0 o 1)
      valueToSave = (editValue === 'true' || editValue === true || editValue === 1 || editValue === '1') ? 1 : 0;
    } else {
      valueToSave = editValue;
    }

    // Guardar el ID de la fila editada para mantener la posición
    lastEditedRowId.current = citaId;

    try {
      // Los campos email_tras_primera_sesion y entregado_documento_seguimiento están en consultas
      // Necesitamos encontrar o crear la consulta asociada
      if (field === 'email_tras_primera_sesion' || field === 'entregado_documento_seguimiento') {
        // Buscar la consulta asociada a esta cita
        const consultasResponse = await consultasAPI.getAll();
        const consultas = consultasResponse.data || [];
        
        // Buscar consulta que coincida con cliente_id, fecha y numero_sesion
        const fechaCita = new Date(cita.fecha_hora);
        const fechaCitaStr = fechaCita.toISOString().split('T')[0];
        
        const consultaAsociada = consultas.find(con => 
          con.cliente_id === cita.cliente_id &&
          con.fecha_consulta === fechaCitaStr &&
          con.numero_sesion === cita.numero_sesion
        );

        if (consultaAsociada) {
          // Actualizar la consulta existente
          const updatedConsulta = {
            ...consultaAsociada,
            [field]: valueToSave
          };
          await consultasAPI.update(consultaAsociada.id, updatedConsulta);
        } else {
          // Si no hay consulta asociada, crear una nueva
          // Solo si la cita no está cancelada
          if (cita.estado !== 'cancelada') {
            const nuevaConsulta = {
              cliente_id: cita.cliente_id,
              fecha_consulta: fechaCitaStr,
              hora_consulta: fechaCita.toTimeString().split(' ')[0],
              numero_sesion: cita.numero_sesion,
              modalidad: cita.modalidad || 'Presencial',
              [field]: valueToSave
            };
            await consultasAPI.create(nuevaConsulta);
          } else {
            setError('No se puede editar: la cita está cancelada y no tiene sesión asociada');
            setTimeout(() => setError(null), 3000);
            setEditingCell(null);
            setEditValue('');
            return;
          }
        }
      } else {
        // Para otros campos, actualizar la cita directamente
        const updatedData = {
          ...cita,
          [field]: valueToSave
        };
        await citasAPI.update(citaId, updatedData);
      }

      setSuccess('Campo actualizado correctamente');
      setTimeout(() => setSuccess(null), 3000);
      
      // Recargar citas y luego restaurar la posición
      await loadCitas();
      
      // Restaurar la posición después de que se actualice el DOM
      setTimeout(() => {
        if (lastEditedRowId.current && rowRefs.current[lastEditedRowId.current]) {
          rowRefs.current[lastEditedRowId.current].scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 100);
    } catch (err) {
      setError('Error al actualizar el campo');
      console.error(err);
      setTimeout(() => setError(null), 3000);
    }

    setEditingCell(null);
    setEditValue('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCellBlur();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    }
  };

  const exportToExcel = async () => {
    // Crear un nuevo libro de trabajo
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(`Resumen de ${labels.sesiones}`);

    // Definir las columnas
    worksheet.columns = [
      { header: 'Fecha', key: 'fecha', width: 12 },
      { header: 'Cliente', key: 'cliente', width: 25 },
      { header: 'Duración', key: 'duracion', width: 10 },
      { header: `Número de ${labels.sesion}`, key: 'numero_sesion', width: 18 },
      { header: `Email tras primera ${labels.sesion_lower}`, key: 'email', width: 30 },
      { header: 'Entregado Documento de Seguimiento', key: 'entregado', width: 35 }
    ];

    // Estilo para la primera fila (títulos) - negrita
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF8F9FA' }
    };
    worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };

    // Colores según el estado
    const getRowColor = (estadoCita) => {
      const estado = (estadoCita || '').toLowerCase();
      if (estado === 'completada') {
        return 'FFD4EDDA'; // Verde claro
      } else if (estado === 'cancelada') {
        return 'FFF8D7DA'; // Rojo claro
      }
      return 'FFFFFFFF'; // Blanco
    };

    // Añadir los datos
    const citasOrdenadas = getSortedCitas();
    citasOrdenadas.forEach((cita) => {
      const estadoCita = (cita.estado || '').toLowerCase();
      const rowColor = getRowColor(estadoCita);
      
      const row = worksheet.addRow({
        fecha: formatFecha(cita.fecha_hora),
        cliente: getClienteNombre(cita.cliente_id),
        duracion: formatDuracion(cita.duracion_minutos),
        numero_sesion: cita.numero_sesion || '-',
        email: cita.email_tras_primera_sesion || '',
        entregado: cita.entregado_documento_seguimiento ? 'Sí' : 'No'
      });

      // Aplicar color de fondo a toda la fila
      row.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: rowColor }
      };

      // Aplicar el color a cada celda individualmente
      row.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: rowColor }
        };
      });
    });

    // Generar el nombre del archivo con la fecha y hora actual
    const ahora = new Date();
    const fecha = ahora.toISOString().split('T')[0];
    const hora = ahora.toTimeString().split(' ')[0].replace(/:/g, '-');
    const nombreArchivo = `Resumen_${labels.sesiones}_${fecha}_${hora}.xlsx`;

    // Generar el buffer y descargar
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nombreArchivo;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="loading">Cargando resumen de {labels.sesiones_lower}...</div>;
  }

  return (
    <div className="resumen-sesiones">
      <div className="resumen-header">
        <h2>Resumen de {labels.sesiones}</h2>
        <button 
          onClick={exportToExcel} 
          className="btn-export-excel"
          disabled={citas.length === 0}
        >
          📊 Exportar a Excel
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {citas.length === 0 ? (
        <div className="empty-state">
          <p>No hay citas registradas</p>
        </div>
      ) : (
        <div className="resumen-table-container">
          <table className="resumen-table">
            <thead>
              <tr>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('fecha')}
                >
                  Fecha
                  {sortField === 'fecha' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('cliente')}
                >
                  Cliente
                  {sortField === 'cliente' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </th>
                <th>Duración</th>
                <th>Número de {labels.sesion}</th>
                <th>Email tras primera {labels.sesion_lower}</th>
                <th>Entregado Documento de Seguimiento</th>
              </tr>
            </thead>
            <tbody>
              {getSortedCitas().map((cita) => {
                // Determinar el color de fondo según el estado de la cita
                const estadoCita = (cita.estado || '').toLowerCase();
                let rowClass = 'sesion-agendada'; // Por defecto: blanco (confirmada o sin estado)
                
                if (estadoCita === 'completada') {
                  rowClass = 'sesion-completada'; // Verde
                } else if (estadoCita === 'cancelada') {
                  rowClass = 'sesion-cancelada'; // Rojo
                }
                
                // Determinar si los campos son editables (solo si hay sesión asociada o la cita no está cancelada)
                const tieneSesion = cita.email_tras_primera_sesion !== null || cita.entregado_documento_seguimiento !== null;
                const esEditable = tieneSesion || cita.estado !== 'cancelada';
                
                return (
                <tr 
                  key={cita.id} 
                  className={rowClass}
                  ref={(el) => {
                    if (el) {
                      rowRefs.current[cita.id] = el;
                    }
                  }}
                >
                  <td>{formatFecha(cita.fecha_hora)}</td>
                  <td>{getClienteNombre(cita.cliente_id)}</td>
                  <td>{formatDuracion(cita.duracion_minutos)}</td>
                  <td>{cita.numero_sesion || '-'}</td>
                  <td
                    className={esEditable ? "editable-cell" : ""}
                    onClick={() => {
                      if (esEditable) {
                        handleCellClick(cita.id, 'email_tras_primera_sesion', cita.email_tras_primera_sesion);
                      }
                    }}
                    style={{ cursor: esEditable ? 'pointer' : 'default' }}
                  >
                    {editingCell?.citaId === cita.id && editingCell?.field === 'email_tras_primera_sesion' ? (
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleCellBlur}
                        onKeyDown={handleKeyPress}
                        autoFocus
                        className="cell-input"
                      />
                    ) : (
                      <span className="cell-content">
                        {cita.email_tras_primera_sesion || '-'}
                      </span>
                    )}
                  </td>
                  <td
                    className={esEditable ? "editable-cell" : ""}
                    onClick={() => {
                      if (esEditable) {
                        handleCellClick(cita.id, 'entregado_documento_seguimiento', cita.entregado_documento_seguimiento);
                      }
                    }}
                    style={{ cursor: esEditable ? 'pointer' : 'default' }}
                  >
                    {editingCell?.citaId === cita.id && editingCell?.field === 'entregado_documento_seguimiento' ? (
                      <select
                        value={editValue === 1 || editValue === '1' || editValue === true || editValue === 'true' ? '1' : '0'}
                        onChange={async (e) => {
                          const newValue = e.target.value;
                          setEditValue(newValue);
                          
                          // Guardar el ID de la fila editada para mantener la posición
                          lastEditedRowId.current = cita.id;
                          
                          // Guardar inmediatamente al cambiar el valor
                          const citaToUpdate = citas.find(c => c.id === cita.id);
                          if (citaToUpdate) {
                            // Buscar la consulta asociada
                            const consultasResponse = await consultasAPI.getAll();
                            const consultas = consultasResponse.data || [];
                            
                            const fechaCita = new Date(cita.fecha_hora);
                            const fechaCitaStr = fechaCita.toISOString().split('T')[0];
                            
                            const consultaAsociada = consultas.find(con => 
                              con.cliente_id === cita.cliente_id &&
                              con.fecha_consulta === fechaCitaStr &&
                              con.numero_sesion === cita.numero_sesion
                            );

                            try {
                              if (consultaAsociada) {
                                const updatedConsulta = {
                                  ...consultaAsociada,
                                  entregado_documento_seguimiento: (newValue === '1' || newValue === 1 || newValue === true || newValue === 'true') ? 1 : 0
                                };
                                await consultasAPI.update(consultaAsociada.id, updatedConsulta);
                              } else if (cita.estado !== 'cancelada') {
                                // Crear nueva consulta si no existe
                                const nuevaConsulta = {
                                  cliente_id: cita.cliente_id,
                                  fecha_consulta: fechaCitaStr,
                                  hora_consulta: fechaCita.toTimeString().split(' ')[0],
                                  numero_sesion: cita.numero_sesion,
                                  modalidad: cita.modalidad || 'Presencial',
                                  entregado_documento_seguimiento: (newValue === '1' || newValue === 1 || newValue === true || newValue === 'true') ? 1 : 0
                                };
                                await consultasAPI.create(nuevaConsulta);
                              } else {
                                setError('No se puede editar: la cita está cancelada y no tiene sesión asociada');
                                setTimeout(() => setError(null), 3000);
                                setEditingCell(null);
                                setEditValue('');
                                return;
                              }
                              
                              setSuccess('Campo actualizado correctamente');
                              setTimeout(() => setSuccess(null), 3000);
                              
                              // Recargar citas y luego restaurar la posición
                              await loadCitas();
                              
                              // Restaurar la posición después de que se actualice el DOM
                              setTimeout(() => {
                                if (lastEditedRowId.current && rowRefs.current[lastEditedRowId.current]) {
                                  rowRefs.current[lastEditedRowId.current].scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center'
                                  });
                                }
                              }, 100);
                              
                              setEditingCell(null);
                              setEditValue('');
                            } catch (err) {
                              setError('Error al actualizar el campo');
                              console.error(err);
                              setTimeout(() => setError(null), 3000);
                            }
                          }
                        }}
                        onKeyDown={handleKeyPress}
                        autoFocus
                        className="cell-input"
                      >
                        <option value="0">No</option>
                        <option value="1">Sí</option>
                      </select>
                    ) : (
                      <span className="cell-content">
                        {cita.entregado_documento_seguimiento ? 'Sí' : 'No'}
                      </span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Resumen de horas totales */}
      {citas.length > 0 && (
        <div className="resumen-horas-totales">
          <div className="resumen-horas-card">
            <div className="resumen-horas-header">
              <h3>Resumen de Horas Totales Realizadas</h3>
            </div>
            <div className="resumen-horas-content">
              <div className="resumen-horas-value">
                {formatTiempo(calcularHorasTotales())}
              </div>
              {tipoUsuario !== 'comercial' && (
                <div className="resumen-horas-label">
                  Total de horas de {labels.sesiones_lower} completadas
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ResumenSesiones;

